import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus, faPen, faFileAlt, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import AccountModal from './AccountModal';
import DataService from '../../services/DataService';
import QuoteModal from './QuoteModal';
import Tooltip from '../../components/common/Tooltip';

const AccountList = () => {
    const [accountsWithAddresses, setAccountsWithAddresses] = useState([]);
    const [isModalActive, setIsModalActive] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 10;
    const [isQuoteModalActive, setIsQuoteModalActive] = useState(false);
    const [selectedAccountForQuote, setSelectedAccountForQuote] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [principalTypeId, setPrincipalTypeId] = useState(null);

    const fetchAccounts = useCallback(async () => {
        try {
            const [accounts, pages, settings] = await Promise.all([
                DataService.getAll('accounts', currentPage, pageSize),
                DataService.getTotalPages('accounts', pageSize),
                DataService.getAll('settings')
            ]);
            
            // Trouver l'ID du type "Principal"
            const principalSetting = settings.find(setting => setting.name === "Principal");
            if (principalSetting) {
                setPrincipalTypeId(principalSetting.id);
            }
            
            const accountsWithDetails = await Promise.all(accounts.map(async (account) => {
                const locations = await DataService.filter('locations', { accountId: account.id, isPrimary: true });
                const contacts = await DataService.filter('contacts', { accountId: account.id, isPrimary: true });
                
                const primaryAddress = locations[0];
                const primaryContact = contacts[0];

                return {
                    ...account,
                    primaryAddress: primaryAddress 
                        ? `${primaryAddress.address}, ${primaryAddress.postalCode} ${primaryAddress.city}` 
                        : '',
                    primaryContact: primaryContact
                        ? `${primaryContact.firstName} ${primaryContact.lastName} (${primaryContact.email})`
                        : ''
                };
            }));
            
            setAccountsWithAddresses(accountsWithDetails);
            setTotalPages(pages);
        } catch (error) {
            console.error('Erreur lors de la récupération des accounts:', error);
        }
    }, [currentPage]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleAddAccount = async (accountData) => {
        try {
            // Ajouter le compte avec le champ taxId
            const newAccount = await DataService.add('accounts', {
                name: accountData.name,
                typeId: accountData.typeId,
                taxId: accountData.taxId || '' // Ajout du taxId lors de la création
            });

            // Ajouter les adresses
            for (const addr of accountData.addresses) {
                await DataService.add('locations', {
                    ...addr,
                    accountId: newAccount.id
                });
            }

            // Ajouter les contacts
            for (const contact of accountData.contacts) {
                await DataService.add('contacts', {
                    ...contact,
                    accountId: newAccount.id
                });
            }

            fetchAccounts();
            setIsModalActive(false);
        } catch (error) {
            console.error('Erreur lors de l\'ajout du compte:', error);
        }
    };

    const handleUpdateAccount = async (accountData) => {
        try {
            // Mise à jour du compte avec le champ taxId
            await DataService.update('accounts', accountData.id, {
                name: accountData.name,
                typeId: accountData.typeId,
                taxId: accountData.taxId || '' // Ajout du taxId lors de la mise à jour
            });

            // Récupérer les adresses et contacts existants
            const existingLocations = await DataService.filter('locations', { accountId: accountData.id });
            const existingContacts = await DataService.filter('contacts', { accountId: accountData.id });

            // Comparer et mettre à jour les adresses
            const locationChanges = hasDataChanged(existingLocations, accountData.addresses);
            if (locationChanges) {
                // Supprimer les anciennes adresses
                for (const loc of existingLocations) {
                    await DataService.delete('locations', loc.id);
                }
                // Ajouter les nouvelles adresses
                for (const addr of accountData.addresses) {
                    await DataService.add('locations', {
                        ...addr,
                        accountId: accountData.id
                    });
                }
            }

            // Comparer et mettre à jour les contacts
            const contactChanges = hasDataChanged(existingContacts, accountData.contacts);
            if (contactChanges) {
                // Supprimer les anciens contacts
                for (const contact of existingContacts) {
                    await DataService.delete('contacts', contact.id);
                }
                // Ajouter les nouveaux contacts
                for (const contact of accountData.contacts) {
                    await DataService.add('contacts', {
                        ...contact,
                        accountId: accountData.id
                    });
                }
            }

            fetchAccounts();
            setIsModalActive(false);
        } catch (error) {
            console.error('Erreur lors de la modification du compte:', error);
        }
    };

    // Fonction utilitaire pour comparer deux tableaux 2 comptes
    const hasDataChanged = (existing, updated) => {
        if (existing.length !== updated.length) return true;

        const normalizeObject = (obj) => {
            const normalized = { ...obj };
            delete normalized.id;
            delete normalized.accountId;
            return JSON.stringify(normalized);
        };

        const existingNormalized = existing.map(normalizeObject).sort();
        const updatedNormalized = updated.map(normalizeObject).sort();

        return !existingNormalized.every((item, index) => item === updatedNormalized[index]);
    };

    const handleViewAccount = async (accountId) => {
        try {
            const accountDetails = await DataService.getAccountWithDetails(accountId);
            setSelectedAccount(accountDetails);
            setIsModalActive(true);
        } catch (error) {
            console.error('Erreur lors de la récupération du compte:', error);
        }
    };

    const handleCreateQuote = (account) => {
        setSelectedAccountForQuote(account);
        setIsQuoteModalActive(true);
    };

    const handleQuoteSubmit = async (quoteData) => {
        try {
            // Créer l'opportunité
            const opportunity = await DataService.add('opportunities', {
                name: quoteData.opportunityName,
                accountId: selectedAccountForQuote.id,
                timeStamp: quoteData.timeStamp
            });

            // Créer le devis
            await DataService.add('quotes', {
                quoteCustomId: quoteData.quoteCustomId,
                opportunityId: opportunity.id,
                timeStamp: quoteData.timeStamp,
                status: 'Draft',
                name: quoteData.quoteName,
                isPrimary: true,
                paid: false
            });

            setIsQuoteModalActive(false);
            setSelectedAccountForQuote(null);
        } catch (error) {
            console.error('Erreur lors de la création du devis:', error);
        }
    };

    const handleCloseModal = () => {
        setIsModalActive(false);
        setSelectedAccount(null);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedAccounts = () => {
        if (!sortConfig.key) return accountsWithAddresses;

        return [...accountsWithAddresses].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'name':
                    aValue = a.name;
                    bValue = b.name;
                    break;
                case 'address':
                    aValue = a.primaryAddress;
                    bValue = b.primaryAddress;
                    break;
                case 'contact':
                    aValue = a.primaryContact;
                    bValue = b.primaryContact;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };

    const shouldShowQuoteButton = (account) => {
        return account.typeId !== principalTypeId;
    };

    return (    
        <div className="panel">
            <div className="panel-heading">
                <div className="panel-header-content">
                    <span className="icon-text">
                        <span className="icon">
                            <FontAwesomeIcon icon={faUsers} />
                        </span>
                        <span> Comptes</span>
                    </span>
                    <div className="panel-header-actions">
                        <div className="buttons">
                            <button 
                                className="button is-small" 
                                onClick={() => handlePageChange(0)}
                                disabled={currentPage === 0}
                            >
                                <span className="icon is-small">««</span>
                            </button>
                            <button 
                                className="button is-small" 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                            >
                                <span className="icon is-small">«</span>
                            </button>
                            <button 
                                className="button is-small" 
                                title={`Page ${currentPage + 1} sur ${totalPages}`}
                            >
                                <span className="icon is-small">{currentPage + 1}</span>
                            </button>
                            <button 
                                className="button is-small" 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages - 1}
                            >
                                <span className="icon is-small">»</span>
                            </button>
                            <button 
                                className="button is-small" 
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={currentPage === totalPages - 1}
                            >
                                <span className="icon is-small">»»</span>
                            </button>
                        </div>
                        <button 
                            className="button is-small" 
                            onClick={() => setIsModalActive(true)}
                        >
                            <span className="icon is-small icon-primary">
                                <FontAwesomeIcon icon={faPlus} />
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="panel-block">
                <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto', width: '100%' }}>
                    <table className="table is-fullwidth is-striped is-hoverable mb-0">
                        <thead className="modal-table-header">
                            <tr>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('name')}
                                    >
                                        Nom <FontAwesomeIcon icon={getSortIcon('name')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('address')}
                                    >
                                        Adresse principale <FontAwesomeIcon icon={getSortIcon('address')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('contact')}
                                    >
                                        Contact principal <FontAwesomeIcon icon={getSortIcon('contact')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">
                                    <Tooltip content={
                                        <div>
                                            <strong>Actions disponibles</strong>
                                            <div className="workflow-columns">
                                                <div className="workflow-column">
                                                    <h4>Modifier</h4>
                                                    <ul>
                                                        <li>• Éditer les informations</li>
                                                        <li>• Modifier les adresses</li>
                                                        <li>• Gérer les contacts</li>
                                                    </ul>
                                                </div>
                                                <div className="workflow-column">
                                                    <h4>Créer un devis</h4>
                                                    <ul>
                                                        <li>• Nouvelle opportunité</li>
                                                        <li>• Devis associé au compte</li>
                                                        <li>• Statut brouillon initial</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    }>
                                        Actions
                                    </Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedAccounts().map(account => (
                                <tr key={account.id}>
                                    <td>{account.name}</td>
                                    <td>{account.primaryAddress}</td>
                                    <td>{account.primaryContact}</td>
                                    <td>
                                        <div className="table-actions-container">
                                            <button
                                                className="button is-small table-action-button"
                                                onClick={() => handleViewAccount(account.id)}
                                            >
                                                <span className="icon is-small icon-primary">
                                                    <FontAwesomeIcon icon={faPen} />
                                                </span>
                                            </button>
                                            {shouldShowQuoteButton(account) && (
                                                <button
                                                    className="button is-small table-action-button"
                                                    onClick={() => handleCreateQuote(account)}
                                                >
                                                    <span className="icon is-small icon-primary">
                                                        <FontAwesomeIcon icon={faFileAlt} />
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <AccountModal 
                isActive={isModalActive}
                onClose={handleCloseModal}
                onSubmit={selectedAccount ? handleUpdateAccount : handleAddAccount}
                account={selectedAccount}
            />
            <QuoteModal 
                isActive={isQuoteModalActive}
                onClose={() => setIsQuoteModalActive(false)}
                onSubmit={handleQuoteSubmit}
                accountName={selectedAccountForQuote?.name || ''}
            />
            <div className="panel-footer"></div>
        </div>
    );
};

export default AccountList;
